<script setup lang="ts">
import type { Collections } from '@nuxt/content'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { SplitText } from 'gsap/SplitText'

const { locale, t } = useI18n()
const localePath = useLocalePath()

const { data: projects } = await useAsyncData('projects', async () => {
  const collection = ('projects_' + locale.value) as keyof Collections
  return await queryCollection(collection).all() as Collections['projects_en'][] | Collections['projects_fr'][]
}, {
  watch: [locale],
})

const items = computed(() => (projects.value || []).map((project: any) => ({
  name: project.name,
  to: localePath('/projects/' + project.stem.split('/').pop()),
  site: project.link,
  screen: project.body?.banner ?? project.image,
  phone: project.body?.phone ?? project.image,
  type: project.industry,
  year: /soon|bient/i.test(project.release || '') ? t('global.soon') : project.release,
  summary: project.summary,
  tags: project.services ?? [],
})))

const pad = (value: number) => String(value).padStart(2, '0')

const root = ref<HTMLElement>()
let media: gsap.MatchMedia | undefined

onMounted(() => {
  gsap.registerPlugin(ScrollTrigger, SplitText)
  media = gsap.matchMedia()

  media.add('(prefers-reduced-motion: no-preference)', () => {
    const q = gsap.utils.selector(root.value)
    root.value!.classList.add('works-ready')

    // Header: the title rises letter by letter.
    const title = SplitText.create(q('.works-title')[0], { type: 'chars', mask: 'chars' })
    gsap.timeline({ defaults: { ease: 'expo.out', lazy: false } })
      .from(title.chars, { yPercent: 110, duration: 1.1, stagger: 0.045 }, 0)
      .from(q('.works-intro > *'), { y: 16, opacity: 0, duration: 0.7, stagger: 0.1, ease: 'power3.out' }, 0.45)
      .from(q('.works-rule'), { scaleX: 0, duration: 1.2, ease: 'power3.inOut' }, 0.35)

    q('.works-item').forEach((item) => {
      const inside = gsap.utils.selector(item)

      // Arrival: the window opens, then the phone and the text come in.
      gsap.timeline({ scrollTrigger: { trigger: item, start: 'top 75%', once: true }, defaults: { ease: 'power3.out' } })
        .from(inside('.works-browser'), {
          clipPath: 'inset(14% 14% 14% 14% round 12px)',
          opacity: 0,
          duration: 1.1,
          ease: 'expo.out',
          clearProps: 'clipPath,opacity',
        }, 0)
        .from(inside('.works-phone-in'), { y: 70, opacity: 0, duration: 1, clearProps: 'transform,opacity' }, 0.25)
        .from(inside('.works-name'), { yPercent: 110, duration: 0.9, ease: 'expo.out' }, 0.1)
        .from(inside('.works-text > *'), { y: 16, opacity: 0, duration: 0.6, stagger: 0.07 }, 0.3)

      // While scrolling: the phone drifts in front of the window.
      gsap.fromTo(inside('.works-phone'), { y: 50 }, {
        y: -50,
        ease: 'none',
        scrollTrigger: { trigger: item, start: 'top bottom', end: 'bottom top', scrub: 0.6 },
      })
    })

    gsap.from(q('.works-end > *'), {
      y: 24,
      opacity: 0,
      duration: 0.8,
      stagger: 0.12,
      ease: 'power3.out',
      scrollTrigger: { trigger: q('.works-end')[0], start: 'top 85%', once: true },
    })

    return () => {
      title.revert()
      root.value?.classList.remove('works-ready')
    }
  }, root.value)
})

onBeforeUnmount(() => media?.revert())
</script>

<template>
  <section
    ref="root"
    class="works-root mx-auto max-w-7xl overflow-x-clip px-4 pb-24 sm:px-6 lg:px-8"
  >
    <header class="pt-10 sm:pt-24">
      <h1 class="works-in works-title text-[clamp(4.5rem,19vw,16rem)] font-semibold leading-[0.95] tracking-tighter text-white">
        <slot
          name="title"
          mdc-unwrap="p"
        />
      </h1>
      <div class="works-in works-rule mt-6 h-px origin-left bg-white/20" />
      <div class="works-in works-intro mt-5 flex flex-wrap items-baseline justify-between gap-x-8 gap-y-1 text-white/60 sm:text-lg">
        <p>
          <slot
            name="subtitle"
            mdc-unwrap="p"
          />
        </p>
        <p class="tabular-nums">
          {{ pad(items.length) }} {{ $t("global.projects_count") }}
        </p>
      </div>
    </header>

    <article
      v-for="(project, index) in items"
      :key="project.to"
      class="works-item grid items-center gap-x-16 gap-y-14 border-b border-white/10 py-20 lg:grid-cols-12 lg:py-32"
    >
      <!-- text -->
      <div
        class="lg:col-span-4"
        :class="index % 2 ? 'lg:order-2' : ''"
      >
        <h2 class="overflow-hidden pb-1">
          <NuxtLink
            :to="project.to"
            class="works-name block text-balance text-5xl font-semibold leading-[1.05] tracking-tight text-white xl:text-6xl"
          >
            {{ project.name }}
          </NuxtLink>
        </h2>
        <div class="works-text">
          <p class="mt-5 text-sm text-white">
            {{ project.type }} · {{ project.year }}
          </p>
          <p class="mt-3 text-pretty leading-relaxed text-white/60">
            {{ project.summary }}
          </p>
          <ul
            v-if="project.tags.length"
            class="mt-5 flex flex-wrap gap-2"
          >
            <li
              v-for="tag in project.tags"
              :key="tag"
              class="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/70"
            >
              {{ tag }}
            </li>
          </ul>
          <div class="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 font-medium">
            <NuxtLink
              :to="project.to"
              class="group inline-flex items-center gap-2 text-white"
            >
              {{ $t("global.see_more") }}
              <UIcon
                name="heroicons:arrow-right"
                class="size-4 transition-transform duration-300 group-hover:translate-x-1"
              />
            </NuxtLink>
            <a
              v-if="project.site"
              :href="project.site"
              target="_blank"
              rel="noopener"
              class="group inline-flex items-center gap-1.5 text-white/60 transition-colors hover:text-white"
            >
              {{ $t("global.project_visit_site") }}
              <UIcon
                name="heroicons:arrow-up-right"
                class="size-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              />
            </a>
          </div>
        </div>
      </div>

      <!-- visual: the same project on a large screen and on a phone -->
      <NuxtLink
        :to="project.to"
        :aria-label="project.name"
        class="group relative block lg:col-span-8"
        :class="index % 2 ? 'lg:order-1 pl-[10%]' : 'pr-[10%]'"
      >
        <div class="works-browser overflow-hidden rounded-xl border border-white/10 bg-neutral-900 shadow-2xl shadow-black/60">
          <div class="flex h-8 items-center gap-1.5 border-b border-white/10 px-3">
            <span
              v-for="dot in 3"
              :key="dot"
              class="size-2 rounded-full bg-white/20"
            />
          </div>
          <div class="aspect-video overflow-hidden">
            <NuxtImg
              :src="project.screen"
              width="1280"
              densities="x1"
              :loading="index === 0 ? 'eager' : 'lazy'"
              :alt="project.name"
              class="size-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-[1.03]"
            />
          </div>
        </div>
        <div
          class="works-phone absolute bottom-[-6%] w-[24%]"
          :class="index % 2 ? 'left-0' : 'right-0'"
        >
          <ProjectPhone class="works-phone-in w-full">
            <NuxtImg
              :src="project.phone"
              width="480"
              densities="x1"
              loading="lazy"
              alt=""
              class="size-full object-cover object-top"
            />
          </ProjectPhone>
        </div>
      </NuxtLink>
    </article>

    <!-- closing -->
    <div class="works-end flex flex-col items-start gap-8 pt-24 lg:flex-row lg:items-end lg:justify-between">
      <p class="max-w-3xl text-balance text-5xl font-semibold leading-[1.05] tracking-tight text-white sm:text-6xl xl:text-7xl">
        {{ t("global.project_next") }}
      </p>
      <NuxtLinkLocale
        to="/contact"
        class="group flex h-14 shrink-0 items-center gap-4 rounded-full bg-white pl-6 pr-2 font-medium text-neutral-950"
      >
        {{ t("agency.cta") }}
        <span class="flex size-10 items-center justify-center rounded-full bg-neutral-950 text-white">
          <UIcon
            name="heroicons:arrow-right"
            class="size-4 transition-transform duration-300 group-hover:translate-x-0.5"
          />
        </span>
      </NuxtLinkLocale>
    </div>
  </section>
</template>

<style>
  /* Hidden until the entrance starts, with a fallback in case the script never runs. */
  @media (prefers-reduced-motion: no-preference) {
    .works-root:not(.works-ready) .works-in {
      opacity: 0;
      animation: works-fallback 0.3s 2.5s forwards;
    }
  }

  @keyframes works-fallback {
    to {
      opacity: 1;
    }
  }
</style>
